import { Suspense, useContext, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { History } from "history";
import { TFunction, useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { apiOrganization } from "src/services/Api/Organization/apiOrganization";
import { wrapPromise } from "go-core";
import FormatResourceStatusLabel from "go-app/components/FormatResourceStatus/FormatResourceStatus";
import Header, { ButtonProps } from "go-app/components/Header";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import FormatDate from "go-core/components/Formatters/FormatDate";
import { LoadingContainer } from "go-core/components/Loading";
import { useWindowSize } from "go-core/components/useWindowSize";
import { ListData } from "go-list/list";
import { ListConfig } from "go-list/list/services/types";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { OrganizationState } from "go-security/services/organizations/types";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext, SegmentListService } from "go-segment/context";
import { ArchivesApi, ArchivesStatus } from "../../../../../../../../../../services/Api/Organization/types";

interface ListState {
	resource?: Record<string, any>;
}

const listName = "ARCHIVE";
const resourceType = "ARCHIVE";

export const getArchiveStatusClassName = (status: ArchivesStatus) => {
	switch (status) {
		case "CLOSED":
			return "success";
		case "OPEN":
			return "blue";
		case "IN_PROGRESS":
			return "warning";
		case "CLOSING":
		default:
			return "danger";
	}
};

const buildConfig = (
	t: TFunction,
	data: Record<string, any>,
	segmentListService: SegmentListService,
	history: History,
	organization: OrganizationState
) => {
	let config = {
		fields: [
			{
				id: "date",
				name: t("common.word.date", { ns: "lib" }),
				render: (item: ArchivesApi) => FormatDate(item.date),
			},
			{
				id: "status",
				name: t("common.word.status", { ns: "lib" }),
				render: (item: ArchivesApi) => (
					<FormatResourceStatusLabel
						status={item.status}
						className={getArchiveStatusClassName(item.status)}
					/>
				),
				renderExport: (item: ArchivesApi) => t(`enums.common.status.${item.status}`),
			},
		],
		actions: [
			{
				name: t("common.action.preview", { ns: "lib" }),
				click: (item: ArchivesApi) => history.push(`${location.pathname}/${item.id}`),
			},
		],
		selectedColumns: ["date", "status"],
		segments: [
			{
				id: "all",
				name: t("common.word.all", { ns: "lib" }),
				slug: "all",
			},
		],
		selectedSegment: "all",
		exportConfig: {
			title: t("modules.archives.field.export_config.title"),
			filename: t("modules.archives.header.title").replace(" ", "_"),
			organization: organization.name,
		},
		fetch: async (params: Record<string, any> = {}, sourceToken?: CancelTokenSource) =>
			apiOrganization.getArchives(params, sourceToken?.token),
		saveSegment: async (segment: SegmentType) => segmentListService.save(listName, resourceType, segment),
	} as ListConfig;
	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...data.fields] : data.fields,
		customFields: data.fields,
		filterValues: data.filter_values,
	};
	return config;
};
const List = ({ resource }: ListState) => {
	const { t } = useTranslation();
	const history = useHistory();
	const location = useLocation();
	const segmentContextValue = useContext(SegmentContext);
	const isMobile = useWindowSize().isMobile;
	const organization = useSelector(selectOrganization);

	if (!resource) return null;

	const data = resource.read();
	const config = buildConfig(t, data, segmentContextValue, history, organization);

	const buttons: ButtonProps[] = [
		{
			title: t("common.action.add", { ns: "lib" }),
			path: `${location.pathname}/new`,
			variant: "primary",
		},
	];

	const mobileActions = [
		{
			title: t("common.action.add", { ns: "lib" }),
			path: `${location.pathname}/new`,
		},
	];

	return (
		<>
			{!isMobile && <Header title={t("modules.archives.header.title")} buttons={isMobile ? [] : buttons} />}
			<ListData
				config={config}
				emptyList={{
					addAction: () => {
						history.push(`${location.pathname}/new`);
					},
				}}
				mobileActions={mobileActions}
			/>
		</>
	);
};

export const OrganizationSettingsArchivesIndexPage = () => {
	const { t } = useTranslation();
	const [resource, setResource] = useState<Record<string, any>>();
	const segmentContextValue = useContext(SegmentContext);
	const { handleChangeTabTitle } = useBrowserTabTitle();

	useEffect(() => {
		handleChangeTabTitle(t("modules.archives.header.title"));
		setResource(wrapPromise(segmentContextValue.get(listName, resourceType)));
	}, []);

	return (
		<div className="content">
			<Suspense fallback={<LoadingContainer />}>
				<List resource={resource} />
			</Suspense>
		</div>
	);
};
